import {useFlagOrCookie} from "@khan/analytics-growthbook";

// sync-start:intl-wham-allowed-countries 1082022591 https://github.com/Khan/webapp/blob/master/services/ai-guide/permissions/is_wham_eligible_country.go

const _khanmigoAccessAllowedCountries: Record<string, true> = {
    us: true,
    gb: true,
    ca: true,
    au: true,
    sg: true,
    nz: true,
    ie: true,
    ng: true,
    tz: true,
    za: true,
    ke: true,
    ug: true,
    gh: true,
    mw: true,
    zm: true,
    zw: true,
    pg: true,
    sl: true,
    lr: true,
    gm: true,
    jm: true,
    bw: true,
    tt: true,
    fj: true,
    gy: true,
    sb: true,
    bz: true,
    bs: true,
    bb: true,
    lc: true,
    gu: true,
    ki: true,
    gd: true,
    je: true,
    vc: true,
    ag: true,
    im: true,
    dm: true,
    ky: true,
    bm: true,
    kn: true,
    fk: true,
    as: true, // American Samoa
    mh: true, // Marshall Islands
    mp: true, // Northern Mariana Islands
    pw: true, // Palau
    pr: true, // Puerto Rico
    vi: true, // US Virgin Islands
    fm: true, // Federated States of Micronesia
    pk: true, // Pakistan
    in: true, // India
    ph: true, // Philippines
    // LATAM
    ar: true, // Argentina
    bo: true, // Bolivia
    cl: true, // Chile
    co: true, // Colombia
    cr: true, // Costa Rica
    ec: true, // Ecuador
    sv: true, // El Salvador
    gt: true, // Guatemala
    hn: true, // Honduras
    mx: true, // Mexico
    ni: true, // Nicaragua
    pa: true, // Panama
    py: true, // Paraguay
    pe: true, // Peru
    do: true, // Dominican Republic
    uy: true, // Uruguay
    ve: true, // Venezuela
    es: true, // Spain
    br: true, // Brazil
    rw: true, // Rwanda
    vn: true, // Vietnam
};

const _khanmigoAccessBlockedCountries: Record<string, true> = {
    cu: true, // Cuba
    ir: true, // Iran
    kp: true, // North Korea
    ru: true, // Russia
    sy: true, // Syria
};

// Includes all the countries that are eligible for Khanmigo, but any
// countries with a false value will not be included in marketing contexts.
// e.g. br: false, if we don't want to show Khanmigo in Brazil.
const _marketingAllowedCountries: Record<string, boolean> = {
    ..._khanmigoAccessAllowedCountries,
};

// Non-hook versions for consumers (like Writing Coach) that should not depend
// on the WHAM feature flag. These implement the "flag off" behavior.
export const isCountryBlocked = (countryCode: string): boolean => {
    const normalizedCountry = countryCode.toLowerCase();
    return !_khanmigoAccessAllowedCountries[normalizedCountry];
};

export const isKhanmigoMarketingAllowedInCountry = (
    countryCode: string,
): boolean => {
    const normalizedCountry = countryCode.toLowerCase();
    return !!_marketingAllowedCountries[normalizedCountry];
};

// Hook versions that *do* respect the WHAM feature flag.
// Allow all the specific countries from our allowed countries list, unless
// overridden by the WHAM "rest of world" behavior.
export const useIsCountryBlocked = (countryCode: string): boolean => {
    const normalizedCountry = countryCode.toLowerCase();
    const whamEligibleRestOfWorld = useFlagOrCookie(
        "wham-eligible-rest-of-world-fe",
    );

    if (whamEligibleRestOfWorld) {
        return !!_khanmigoAccessBlockedCountries[normalizedCountry];
    }

    return isCountryBlocked(normalizedCountry);
};

// Returns true if the country is allowed to see Khanmigo marketing.
export const useIsKhanmigoMarketingAllowedInCountry = (
    countryCode: string,
): boolean => {
    const normalizedCountry = countryCode.toLowerCase();
    const whamEligibleRestOfWorld = useFlagOrCookie(
        "wham-eligible-rest-of-world-fe",
    );

    if (whamEligibleRestOfWorld) {
        return !_khanmigoAccessBlockedCountries[normalizedCountry];
    }

    return isKhanmigoMarketingAllowedInCountry(normalizedCountry);
};
// sync-end:intl-wham-allowed-countries
